import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import DefaultApiParameters from '../../../../enum/DefaultApiParameters';
import currentTimeZone from '../../../../enum/timeZone';
import { ToggleListProperties } from '../../../../interface/device-management.interface';
import {
  DeviceTransactionProperties,
  DeviceTransactionRequest,
} from '../../../../interface/device-transactions.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchDeviceTransaction,
  fetchListTransactionFilters,
  updateTransactionFilters,
  setSortData,
} from '../../../../redux/slices/DeviceTransactionSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DurationToggle from '../../../../shared-components/duration-toggle/DurationToggle';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import device from '../../../../utils/api/device';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  year?: number;
  month?: string;
  date: string;
  hour: number;
  label?: string;
  totalPowerConsumed: number;
  totalDebitAmount: number;
}

/**
 * Function to fill missing months in chart
 * @param data
 * @returns
 */
function fillMissingMonths(data: ChartDataProperties[]) {
  const today = new Date();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate past 12 months dynamically
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1); // Get the first day of the month
    past12Months.push({
      label: months[date.getMonth()], // Month name
      year: date.getFullYear(), // Year
    });
  }

  // Map input data for easy lookup
  const totalPowerConsumedMap = new Map(data.map((item) => [item.month, item.totalPowerConsumed]));
  const totalDebitAmountMap = new Map(data.map((item) => [item.month, item.totalDebitAmount]));

  // Generate result array with missing months filled
  const result = past12Months.map(({ label, year }) => {
    const key = label; // Use the month name as key
    return {
      hour: 0,
      date: '',
      label: `${label} ${year}`, // Combine month and year for output
      totalPowerConsumed: totalPowerConsumedMap.get(key) || 0, // Default to 0 if month is missing
      totalDebitAmount: totalDebitAmountMap.get(key) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to generate the map data for past 30 days
 * @param data
 * @returns
 */
function fillMissingLastMonth(data: ChartDataProperties[]) {
  const today = new Date();
  const past30Days = [];

  // Generate the past 30 days as ISO date strings
  for (let index = 0; index < 30; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    const isoDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    past30Days.push({ date, isoDate });
  }

  // Map input data using full ISO date for lookup
  const totalPowerConsumedMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.totalPowerConsumed]),
  );
  const totalDebitAmountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.totalDebitAmount]),
  );

  // Date formatter to get "Mar 18" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = past30Days.map(({ date, isoDate }) => ({
    hour: 0,
    date: dateFormatter.format(date),
    label: dateFormatter.format(date), // Format label as "Mar 18"
    totalPowerConsumed: totalPowerConsumedMap.get(isoDate) || 0, // Default to 0
    totalDebitAmount: totalDebitAmountMap.get(isoDate) || 0, // Default to 0
  }));

  return result.reverse();
}

/**
 * Function to add missing data in last 7 days
 * @param data
 * @returns
 */
function fillMissingLast7Days(data: ChartDataProperties[]) {
  const today = new Date();
  const last7Days = [];

  // Generate the last 7 days as ISO date strings
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    last7Days.push(date);
  }

  // Map input data using full ISO date for lookup
  const totalPowerConsumedMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.totalPowerConsumed]),
  );
  const totalDebitAmountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.totalDebitAmount]),
  );
  // Date formatter to get "Nov 14" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = last7Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
    return {
      hour: 0,
      date: dateFormatter.format(date),
      label: dateFormatter.format(date), // Format label as "Mar 18"
      totalPowerConsumed: totalPowerConsumedMap.get(isoDate) || 0, // Default to 0
      totalDebitAmount: totalDebitAmountMap.get(isoDate) || 0, // Default to 0
    };
  });

  return result.reverse();
}

/**
 * Function to handle missing data for the last 24 hours based on the current time zone
 * @param {ChartDataProperties[]} data - The input data
 * @param {string} timeZone - The time zone (e.g., "Asia/Kolkata", "America/New_York")
 * @returns {object[]} The result with complete data for the last 24 hours
 */
function fillMissingLast24HrsData(data: ChartDataProperties[]) {
  const now = new Date();
  const past24Hours = [];

  for (let index = 0; index < 24; index += 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - index, 0, 0, 0); // Adjust hour, reset minutes/seconds/ms

    // Format date and hour based on the specified time zone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: currentTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23', // Ensures 0-23 hour format
    });

    const formattedDate = formatter.formatToParts(date);
    const year = formattedDate.find((p) => p.type === 'year')?.value;
    const month = formattedDate.find((p) => p.type === 'month')?.value;
    const day = formattedDate.find((p) => p.type === 'day')?.value;
    const hour = formattedDate.find((p) => p.type === 'hour')?.value;

    // Ensure YYYY-MM-DD format
    const isoDate = `${year}-${month}-${day}`;

    past24Hours.push({ date: isoDate, hour: Number(hour) });
  }

  // Build the result array
  const result = past24Hours.map((eachHour) => {
    const dataForHour = data.find(
      (eachData) => eachData.date === eachHour.date && eachData.hour === eachHour.hour,
    );

    return {
      hour: 0,
      date: '',
      label: eachHour.hour.toString(), // Hour (0-23)
      totalPowerConsumed: dataForHour ? dataForHour.totalPowerConsumed : 0, // Default to 0 if data not found
      totalDebitAmount: dataForHour ? dataForHour.totalDebitAmount : 0, // Default to 0 if data not found
    };
  });

  return result.reverse(); // Ensuring chronological order
}

/**
 * Transaction component
 * @returns
 */
function Transactions() {
  const branding = useAppBranding();
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const {
    transactions,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    sortField,
    sortBy,
  } = useAppSelector((s) => s.deviceTransaction);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);

  const [powerConsumedRevenue, setPowerConsumedRevenue] = useState<ChartDataProperties[]>([]);

  /** Draws the vertical crosshair line using the theme's success color on hover */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a success-colored vertical crosshair line at the hovered x position.
     * @param chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successColor || '#2E9E3A';
        context.stroke();
        context.restore();
      }
    },
  };

  const lineChartContainerReference = useRef<HTMLDivElement>(null);
  const [lineChartSize, setLineChartSize] = useState({
    width: 0,
    height: 0,
  });

  const [toggleList, setToggleList] = useState<ToggleListProperties[]>([
    {
      label: t('last24Hours'),
      key: 'last24hours',
      isActive: true,
    },
    {
      label: t('last7Days'),
      key: 'last7days',
      isActive: false,
    },
    {
      label: t('lastMonth'),
      key: 'lastMonth',
      isActive: false,
    },
    {
      label: t('last12Months'),
      key: 'last12months',
      isActive: false,
    },
  ]);

  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof DeviceTransactionProperties,
      sortAllow: true,
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof DeviceTransactionProperties,
      sortAllow: true,
    },
    {
      label: t('connector'),
      accessorKey: 'connector' as keyof DeviceTransactionProperties,
      sortAllow: true,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof DeviceTransactionProperties,
      sortAllow: true,
      /**
       * Function to show custom data in table
       * Converts power consumed from Wh to Kwh
       * @param row
       * @returns
       */
      customData: (row: DeviceTransactionProperties) => (
        <p>{ToFixedValue(Number(row?.powerConsumed || 0.0) / 1000)} kWh</p>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount' as keyof DeviceTransactionProperties,
      sortAllow: true,
      /**
       * Function to show custom data in table
       * Converts power consumed from Wh to Kwh
       * @param row
       * @returns
       */
      customData: (row: DeviceTransactionProperties) => (
        <p>
          {ToFixedValue(Number(row?.amount || 0.0))} {branding.text.currency}
        </p>
      ),
    },
  ];

  /**
   * Function to change the active menu
   * @param key
   */
  const handleClickFunction = (key: string) => {
    setToggleList((previousValue) =>
      previousValue.map((eachValue) => {
        let updatedValue = eachValue;
        if (eachValue.key === key) {
          updatedValue = {
            ...eachValue,
            isActive: true,
          };
        } else {
          updatedValue = {
            ...eachValue,
            isActive: false,
          };
        }
        return updatedValue;
      }),
    );
  };

  const revenueChartData = {
    labels: powerConsumedRevenue.map((eachData) => eachData.label),
    /**
     * data set function
     * @param values
     * @returns
     */
    datasets: [
      {
        label: t('revenue'),
        data: powerConsumedRevenue.map((d) => ToFixedValue(d.totalDebitAmount)),
        borderColor: '#F79009',
        /**
         * Background
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const powerConsumedChartData = {
    labels: powerConsumedRevenue.map((eachData) => eachData.label),
    /**
     * data set function
     * @param values
     * @returns
     */
    datasets: [
      {
        label: t('electricity'),
        data: powerConsumedRevenue.map((d) => ToFixedValue(d.totalPowerConsumed)),
        borderColor: '#2E9E3A',
        /**
         * Function to create a gradient bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const chartContext = chart.ctx;
          const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
          gradient.addColorStop(0, 'rgba(46, 158, 58, 1)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0, // Remove points on the line
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: true,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param context
           * @returns
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel;
          },
          /**
           * Function to show tooltip data
           * @param context
           * @returns
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === t('revenue') ? branding.text.currency : 'kWh'}`;
          },
        },
      },
    },

    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchDeviceTransaction({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: DeviceTransactionRequest) => {
    dispatch(
      fetchDeviceTransaction({
        ...value,
        sortField,
        sortBy,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Function to fetch report for charging schedule
   */
  const handleFetchTransactionReport = async (timePeriod: string) => {
    try {
      const response = await device.fetchTransactionReport({
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        timeZone: currentTimeZone,
        timePeriod,
      });
      let finalData: ChartDataProperties[] = [];
      if (timePeriod === 'last12months') {
        finalData = fillMissingMonths(response.data);
      } else if (timePeriod === 'lastMonth') {
        finalData = fillMissingLastMonth(response.data);
      } else if (timePeriod === 'last7days') {
        finalData = fillMissingLast7Days(response.data);
      } else if (timePeriod === 'last24hours') {
        finalData = fillMissingLast24HrsData(response.data);
      }
      setPowerConsumedRevenue(finalData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleFetchTransactionReport(
      toggleList.find((toggle) => toggle.isActive === true)?.key || 'last24hours',
    );
  }, [toggleList]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchDeviceTransaction({
        page: DefaultApiParameters.page,
        limit: DefaultApiParameters.limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
        ...queryParameters,
      }),
    );
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    const width = lineChartContainerReference.current?.offsetWidth || 0;
    const height = lineChartContainerReference.current?.offsetHeight || 0;
    setLineChartSize({
      width: width - 25,
      height,
    });
  }, [lineChartContainerReference.current]);

  return (
    <div className="device-details-transaction-wrap">
      <div className="device-details-transaction-table">
        <CustomTable
          header={column}
          data={transactions}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateTransactionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListTransactionFilters}
          version="v2"
        />
      </div>
      <div className="device-details-container" ref={lineChartContainerReference}>
        <DurationToggle toggleList={toggleList} handleClickFunction={handleClickFunction} />
        <div className="chart-container">
          <h6>{t('powerConsumed')}</h6>
          <Line
            data={powerConsumedChartData}
            options={options}
            width={lineChartSize.width}
            height={200}
            plugins={[verticalHoverLinePlugin]}
          />
        </div>
        <div className="chart-container">
          <h6>{t('revenueGraph')}</h6>
          <Line
            data={revenueChartData}
            options={options}
            width={lineChartSize.width}
            height={200}
            plugins={[verticalHoverLinePlugin]}
          />
        </div>
      </div>
    </div>
  );
}

export default Transactions;
